"use client";

import { MapContainer, TileLayer, Polyline, Popup, Marker } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { siteConfig } from "@/config/siteConfig";
import "leaflet/dist/leaflet.css";

// CSS Animation for pulsing orbs
const pulseStyle = `
  @keyframes pulse-orb {
    0%, 100% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.3);
      opacity: 0.4;
    }
  }
  .pulse-orb {
    animation: pulse-orb 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
`;

// Inject CSS into document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseStyle;
  document.head.appendChild(style);
}

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

// OSRM API for real road routing
const OSRM_API = "https://router.project-osrm.org/route/v1/driving";

// Brand colors
const BRAND_COLOR = "#A91E59"; // Burgundy (primary brand)
const SECONDARY_COLOR = "#F5A623"; // Orange (secondary brand)

// Fetch route coordinates from OSRM (snapped to actual roads) with waypoints
async function getOSRMRoute(coordinates: [number, number][]): Promise<[number, number][]> {
  try {
    // Waypoints are [lng, lat], OSRM format: lng,lat;lng,lat
    const coordinateString = coordinates.map((coord) => `${coord[0]},${coord[1]}`).join(";");
    const url = `${OSRM_API}/${coordinateString}?geometries=geojson&overview=full`;
    console.log("Fetching OSRM route:", url);
    
    const response = await fetch(url);
    const data = await response.json();
    
    console.log("OSRM Response:", data);
    
    if (data.routes && data.routes.length > 0) {
      // OSRM returns coordinates as [lng, lat], convert to [lat, lng] for Leaflet
      const routePoints = data.routes[0].geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
      console.log("Route points:", routePoints);
      return routePoints;
    }
  } catch (error) {
    console.error("Error fetching route from OSRM:", error);
  }
  return [];
}

// Define route coordinates (approximated based on known locations)
const routeCoordinates = [
  // Route 1: Johannesburg CBD → Bez Valley → Maputo
  {
    id: 1,
    name: "Johannesburg → Maputo",
    color: BRAND_COLOR,
    // Waypoints: [lng, lat] format for OSRM
    waypoints: [
      [28.0473, -26.2041], // Johannesburg CBD (start)
      [28.0919, -26.1887], // Bez Valley (pickup)
      [32.433741, -25.900349], // Matola - N4 Tchumene (stop 1)
      [32.460389, -25.953759], // Matola - Bairro Hanhane (stop 2)
      [32.563181, -25.968430], // Maputo (final)
    ],
    stops: [
      {
        name: "Johannesburg (CBD)",
        lat: -26.2041,
        lng: 28.0473,
        address: "95 Kerk Street, JHB CBD",
      },
      {
        name: "Johannesburg (Bez Valley)",
        lat: -26.1887,
        lng: 28.0919,
        address: "1105 Albertina Sisulu Road, Bez Valley, JHB",
      },
      {
        name: "Matola - N4 Tchumene",
        lat: -25.900349,
        lng: 32.433741,
        address: "N4 Tchumene, Matola, Mozambique",
      },
      {
        name: "Matola - Bairro Hanhane",
        lat: -25.953759,
        lng: 32.460389,
        address: "Preceta Herculano, Bairro Hanhane, Matola",
      },
      {
        name: "Maputo",
        lat: -25.968430,
        lng: 32.563181,
        address: "Rua João Mulungu 451, Maputo",
      },
    ],
  },
  // Route 2: Johannesburg Bez Valley → CBD → Maputo (reverse pickup order)
  {
    id: 2,
    name: "Maputo → Johannesburg",
    color: SECONDARY_COLOR,
    // Waypoints: [lng, lat] format for OSRM
    waypoints: [
      [32.563181, -25.968430], // Start
      [32.460389, -25.953759], // Pickup 1
      [32.433741, -25.900349], // Pickup 2
      [28.0500, -26.2500], // Local road entry (balanced distance)
      [28.0563751, -26.2434592], // Stop 1: Rosettenville
      [28.0473, -26.2041], // Stop 2: CBD
      [28.0919, -26.1887], // Final: Bez Valley
    ],
  },
];

export default function RoutesMap() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stopsCollapsed, setStopsCollapsed] = useState(true);
  const [busPositions, setBusPositions] = useState<{ [key: number]: [number, number] }>({});
  const [shownMarkerType, setShownMarkerType] = useState<{ [key: string]: 'pickup' | 'dropoff' }>({});

  // Calculate map center (between Johannesburg and Maputo)
  const center = [-26.0, 30.3] as L.LatLngExpression;

  // Fetch routes from OSRM on component mount
  useEffect(() => {
    async function fetchRoutes() {
      setIsLoading(true);
      try {
        const updatedRoutes = await Promise.all(
          routeCoordinates.map(async (route) => {
            const points = await getOSRMRoute(route.waypoints as [number, number][]);
            return {
              ...route,
              points: points.length > 0 ? points : route.waypoints.map((w) => [w[1], w[0]]),
            };
          })
        );
        setRoutes(updatedRoutes);
      } catch (error) {
        console.error("Error fetching routes:", error);
        // Fallback to waypoint coordinates if OSRM fails
        setRoutes(
          routeCoordinates.map((route) => ({
            ...route,
            points: route.waypoints.map((w) => [w[1], w[0]]),
          }))
        );
      } finally {
        setIsLoading(false);
      }
    }

    fetchRoutes();
  }, []);

  // Animate buses along routes
  useEffect(() => {
    if (routes.length === 0) return;

    let animationFrameId: number;
    let startTime = Date.now();

    const animateBuses = () => {
      const elapsed = (Date.now() - startTime) / 1000; // seconds
      const routeDuration = 30; // each route takes 30 seconds to complete
      const cycle = (elapsed % routeDuration) / routeDuration; // 0 to 1

      const newPositions: { [key: number]: [number, number] } = {};

      routes.forEach((route) => {
        if (route.points && route.points.length > 1) {
          // Calculate which segment we're on
          const segmentIndex = Math.floor(cycle * (route.points.length - 1));
          const nextSegmentIndex = Math.min(segmentIndex + 1, route.points.length - 1);

          // Interpolate between the two points for smooth movement
          const segmentProgress = (cycle * (route.points.length - 1)) % 1;
          const currentPoint = route.points[segmentIndex];
          const nextPoint = route.points[nextSegmentIndex];

          const lat = currentPoint[0] + (nextPoint[0] - currentPoint[0]) * segmentProgress;
          const lng = currentPoint[1] + (nextPoint[1] - currentPoint[1]) * segmentProgress;

          newPositions[route.id] = [lat, lng];
        }
      });

      setBusPositions(newPositions);
      animationFrameId = requestAnimationFrame(animateBuses);
    };

    animationFrameId = requestAnimationFrame(animateBuses);

    return () => cancelAnimationFrame(animationFrameId);
  }, [routes]);

  return (
    <div className="min-h-screen bg-background pb-20 sm:pb-0">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 sm:py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="rounded-full"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Our Routes</h1>
            <p className="text-sm text-muted-foreground">
              View all available routes and stops across our network
            </p>
          </div>
        </div>
      </div>

      <div className="relative h-[calc(100vh-80px)]">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/50 backdrop-blur-sm">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading routes...</p>
            </div>
          </div>
        )}

        {/* Map */}
        <MapContainer
          center={center}
          zoom={6}
          scrollWheelZoom={true}
          zoomControl={false}
          attributionControl={false}
          style={{ height: "100%", width: "100%" }}
          className="z-10"
          {...({} as any)}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, tiles style by <a href="https://www.hotosm.org/" target="_blank" rel="noopener noreferrer">Humanitarian OpenStreetMap Team</a>, hosted by <a href="https://openstreetmap.fr/" target="_blank" rel="noopener noreferrer">OpenStreetMap France</a>'
            {...({} as any)}
          />

          {/* Routes */}
          {routes.map((route) => {
            const serviceRoute = siteConfig.routes.find(r => r.id === route.id);
            
            return (
            <div key={route.id}>
              {/* Route Line Glow (underlay for visual effect) */}
              {route.points && route.points.length > 0 && (
                <Polyline
                  positions={route.points as L.LatLngExpression[]}
                  pathOptions={{
                    color: route.color,
                    weight: 10,
                    opacity: 0.15,
                  }}
                  {...({} as any)}
                />
              )}

              {/* Route Line (snapped to roads via OSRM) */}
              {route.points && route.points.length > 0 && (
                <Polyline
                  positions={route.points as L.LatLngExpression[]}
                  pathOptions={{
                    color: route.color,
                    weight: 5,
                    opacity: 0.9,
                    lineCap: "round",
                    lineJoin: "round",
                    dashArray: route.id === 2 ? "8, 4" : undefined,
                  }}
                  {...({} as any)}
                >
                  <Popup sticky {...({} as any)}>
                    <div className="font-semibold text-sm whitespace-nowrap">{serviceRoute?.from} → {serviceRoute?.to}</div>
                  </Popup>
                </Polyline>
              )}

              {/* Pickup Points with MapPin Icons */}
              {serviceRoute?.pickupPoints.map((pickup: any, idx: number) => {
                // Use coordinate-only key for cross-route matching
                const coordKey = `${pickup.lat}-${pickup.lng}`;
                
                // Find matching dropoff in OTHER routes only (not same route)
                let matchingDropoff = null;
                for (const otherRoute of siteConfig.routes) {
                  if (otherRoute.id !== route.id) {
                    const match = otherRoute.dropoffPoints.find(
                      (d: any) => d.lat === pickup.lat && d.lng === pickup.lng
                    );
                    if (match) {
                      matchingDropoff = match;
                      break;
                    }
                  }
                }
                
                const shouldShow = !matchingDropoff || shownMarkerType[coordKey] !== 'dropoff';
                
                if (!shouldShow) return null;

                const icon = L.divIcon({
                  html: `<div style="background-color: ${route.color}; color: white; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.25); border: 2px solid white; cursor: pointer;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`,
                  iconSize: [40, 40],
                  popupAnchor: [0, -20],
                  className: 'map-marker-icon',
                });
                return (
                  <Marker
                    key={`${route.id}-pickup-${idx}`}
                    position={[pickup.lat || 0, pickup.lng || 0] as L.LatLngExpression}
                    {...({ icon } as any)}
                    eventHandlers={{
                      click(e: any) {
                        if (matchingDropoff) {
                          setShownMarkerType(prev => ({
                            ...prev,
                            [coordKey]: prev[coordKey] === 'pickup' ? 'dropoff' : 'pickup'
                          }));
                        }
                        e.target.openPopup();
                      },
                      mouseover(e: any) {
                        e.target.openPopup();
                      },
                      mouseout(e: any) {
                        e.target.closePopup();
                      },
                    }}
                  >
                    <Popup {...({} as any)}>
                      <div className="text-xs max-w-xs">
                        <p className="font-bold mb-0.5">{pickup.name}</p>
                        <p className="text-gray-600 mb-1">{pickup.address}</p>
                        {pickup.landmark && (
                          <p className="text-gray-500 italic text-xs mb-1">{pickup.landmark}</p>
                        )}
                        <p className="text-gray-700"><strong>Check-in:</strong> {pickup.checkIn}</p>
                        <p className="text-gray-700"><strong>Departs:</strong> {pickup.departure}</p>
                        {matchingDropoff && (
                          <button
                            className="text-blue-600 text-xs mt-2 cursor-pointer font-semibold hover:underline w-full text-left"
                            onClick={() => {
                              setShownMarkerType(prev => ({
                                ...prev,
                                [coordKey]: 'dropoff'
                              }));
                            }}
                          >
                            → View stop info
                          </button>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Dropoff Points with MapPin Icons */}
              {serviceRoute?.dropoffPoints.map((dropoff: any, idx: number) => {
                // Use coordinate-only key for cross-route matching
                const coordKey = `${dropoff.lat}-${dropoff.lng}`;
                
                // Find matching pickup in OTHER routes only (not same route)
                let matchingPickup = null;
                for (const otherRoute of siteConfig.routes) {
                  if (otherRoute.id !== route.id) {
                    const match = otherRoute.pickupPoints.find(
                      (p: any) => p.lat === dropoff.lat && p.lng === dropoff.lng
                    );
                    if (match) {
                      matchingPickup = match;
                      break;
                    }
                  }
                }
                
                const shouldShow = !matchingPickup || shownMarkerType[coordKey] === 'dropoff';
                
                if (!shouldShow) return null;

                const icon = L.divIcon({
                  html: `<div style="background-color: ${route.color}; color: white; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.25); border: 2px solid white; opacity: 0.8; cursor: pointer;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`,
                  iconSize: [36, 36],
                  popupAnchor: [0, -18],
                  className: 'map-marker-icon',
                });
                return (
                  <Marker
                    key={`${route.id}-dropoff-${idx}`}
                    position={[dropoff.lat || 0, dropoff.lng || 0] as L.LatLngExpression}
                    {...({ icon } as any)}
                    eventHandlers={{
                      click(e: any) {
                        if (matchingPickup) {
                          setShownMarkerType(prev => ({
                            ...prev,
                            [coordKey]: prev[coordKey] === 'dropoff' ? 'pickup' : 'dropoff'
                          }));
                        }
                        e.target.openPopup();
                      },
                      mouseover(e: any) {
                        e.target.openPopup();
                      },
                      mouseout(e: any) {
                        e.target.closePopup();
                      },
                    }}
                  >
                    <Popup {...({} as any)}>
                      <div className="text-xs max-w-xs">
                        <p className="font-bold mb-0.5">{dropoff.name}</p>
                        <p className="text-gray-600 mb-1">{dropoff.address}</p>
                        {dropoff.landmark && (
                          <p className="text-gray-500 italic text-xs mb-1">{dropoff.landmark}</p>
                        )}
                        <p className="text-gray-700"><strong>Arrives:</strong> {dropoff.arrival}</p>
                        {matchingPickup && (
                          <button
                            className="text-blue-600 text-xs mt-2 cursor-pointer font-semibold hover:underline w-full text-left"
                            onClick={() => {
                              setShownMarkerType(prev => ({
                                ...prev,
                                [coordKey]: 'pickup'
                              }));
                            }}
                          >
                            ← View pickup info
                          </button>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Pulsing Orb Markers on Routes - Animated */}
              {route.points && route.points.length > 0 && busPositions[route.id] && (
                <Marker
                  key={`bus-${route.id}`}
                  position={busPositions[route.id] as L.LatLngExpression}
                  {...({
                    icon: L.divIcon({
                      html: `<div class="pulse-orb" style="background-color: ${route.color}; width: 24px; height: 24px; border-radius: 50%; box-shadow: 0 0 12px ${route.color}60, 0 2px 6px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.3); border: 2px solid rgba(255,255,255,0.6);"></div>`,
                      iconSize: [24, 24],
                      popupAnchor: [0, -12],
                      className: '',
                    }),
                  } as any)}
                />
              )}
            </div>
          );
          })}
        </MapContainer>

        {/* Stops List */}
        <div className="absolute top-6 left-6 bg-card/90 backdrop-blur-sm border border-border/50 rounded-lg p-4 z-20 max-w-sm">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="font-semibold text-sm">Route Stops</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStopsCollapsed(!stopsCollapsed)}
              className="h-6 w-6 p-0"
            >
              {stopsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>
          </div>
          
          {!stopsCollapsed && (
            <div className="max-h-96 overflow-y-auto">
              {siteConfig.routes.map((route) => {
                const routeInfo = routeCoordinates.find(r => r.id === route.id);
                return (
                  <div key={route.id} className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      {/* eslint-disable-next-line react/style-prop-object */}
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: routeInfo?.color }}
                      />
                      <p className="font-semibold text-xs">{route.from} → {route.to}</p>
                    </div>
                    
                    <div className="space-y-1 text-xs ml-4 border-l border-border/50 pl-3">
                      {/* Pickup Points */}
                      {route.pickupPoints.map((pickup: any, idx: number) => (
                        <div key={`pickup-${idx}`} className="text-muted-foreground">
                          <span className="font-medium text-foreground">{pickup.name}</span>
                          <div className="text-xs text-muted-foreground">
                            Check-in: {pickup.checkIn} | Depart: {pickup.departure}
                          </div>
                        </div>
                      ))}
                      
                      {/* Dropoff Points */}
                      {route.dropoffPoints.map((dropoff: any, idx: number) => (
                        <div key={`dropoff-${idx}`} className="text-muted-foreground">
                          <span className="font-medium text-foreground">{dropoff.name}</span>
                          <div className="text-xs text-muted-foreground">
                            Arrive: {dropoff.arrival}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="absolute bottom-6 left-6 bg-card/90 backdrop-blur-sm border border-border/50 rounded-lg p-4 z-20 max-w-xs">
          <h3 className="font-semibold mb-3 text-sm">Routes</h3>
          <div className="space-y-2">
            {siteConfig.routes.map((route) => {
              const routeInfo = routeCoordinates.find(r => r.id === route.id);
              return (
                <div key={route.id} className="flex items-center gap-2 text-sm">
                  {/* eslint-disable-next-line react/style-prop-object */}
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: routeInfo?.color }}
                  />
                  <span>{route.from} → {route.to}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Hover over or tap routes and stops to view details
          </p>
        </div>
      </div>
    </div>
  );
}
